import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckIcon, XIcon, CreditCardIcon, AlertCircleIcon,
  LockIcon, CheckCircleIcon, HomeIcon, LogInIcon, UserPlusIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useSubscription, PlanType } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import PurchaseCreditsModal from '../components/modals/PurchaseCreditsModal';
import Footer from '../components/common/Footer';

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentPlan, plans, upgradePlan, loading, userFlags } = useSubscription();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === 'true';
  const canceled = searchParams.get('canceled') === 'true';
  const isComplimentary = userFlags?.plan_override;
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Ensure we scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSelectPlan = (plan: PlanType) => {
    console.log('Selecting plan:', plan, 'Current user:', user?.id);
    
    // If user is not authenticated, redirect to signup
    if (!user) {
      // Store the selected plan in localStorage for after signup
      localStorage.setItem('selectedPlan', plan);
      navigate('/signup', { 
        state: { 
          selectedPlan: plan,
          from: { pathname: '/subscription' }
        } 
      });
      return;
    }

    if (plan === currentPlan) {
      return;
    }
    
    if (plan === 'free') {
      // Handle downgrade to free plan
      // This would need to be implemented via Stripe Customer Portal
      console.log('Free plan selected - no action needed');
      return;
    } else {
      console.log('Upgrading to plan:', plan);
      // Allow upgrades even for users with complimentary access (for testing)
      upgradePlan(plan);
    }
  };

  // Show complimentary access message for authenticated users with overrides
  if (user && isComplimentary) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-md mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Card className="p-8 bg-gradient-to-br from-primary-50 to-secondary-50">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Complimentary Access</h2>
              <div className="inline-flex items-center bg-accent-100 text-accent-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <span className="mr-2">✨</span>
                Pro Features Enabled
              </div>
              <p className="text-gray-600 mb-6">
                You have full access to all Pro features of VoiceVision. Enjoy unlimited sessions,
                advanced AI capabilities, and priority support.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                leftIcon={<HomeIcon className="h-5 w-5" />}
                className="bg-white hover:bg-gray-50"
              >
                Return to Dashboard
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  if (success || canceled) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-md mx-auto"
      >
        {success ? (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-success-50 rounded-lg p-8 text-center"
          >
            <div className="mx-auto w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircleIcon className="h-10 w-10 text-success-600" />
            </div>
            <h2 className="text-xl font-semibold text-success-800 mb-2">Payment Successful!</h2>
            <p className="text-success-700 mb-6">
              Your subscription has been updated. Enjoy your new plan!
            </p>
            <div className="flex justify-center">
              <Button
                variant="primary"
                className="bg-success-600 hover:bg-success-700"
                onClick={() => navigate('/')}
              >
                Go to Dashboard
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="mb-6 bg-error-50 border-l-4 border-error-500 p-4 rounded-md">
            <div className="flex">
              <AlertCircleIcon className="h-5 w-5 text-error-500 mr-2" />
              <p className="text-error-700">Payment was canceled. Please try again.</p>
            </div>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => navigate('/subscription')}
            >
              Return to Plans
            </Button>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gray-50 py-8"
    >
      <div className="max-w-5xl mx-auto px-4">
      {/* Navigation */}
      <motion.div variants={itemVariants} className="mb-8">
        <nav className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Home
          </button>
          {!user && (
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-800 font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Sign Up
              </Link>
            </div>
          )}
        </nav>
      </motion.div>

      <motion.div variants={itemVariants} className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Subscription Plans</h1>
        <p className="text-gray-600 mt-2">
          Choose the plan that best fits your needs
        </p>
        {!user && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Sign up to get started!</strong> View our plans below and create an account to begin your journey.
            </p>
          </div>
        )}
      </motion.div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Free Plan */}
        <motion.div variants={itemVariants}>
          <Card className={`h-full ${user && currentPlan === 'free' ? 'ring-2 ring-primary-500' : ''}`}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Free</h2>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">$0</span>
                <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Perfect for casual users
              </p>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                {plans.free.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-success-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>BYOK Available:</strong> Use your own OpenAI API key for unlimited access
                </p>
              </div>
            </div>
            <div className="p-6 pt-0">
              <Button
                variant={user && currentPlan === 'free' ? 'outline' : 'primary'}
                fullWidth
                disabled={user && currentPlan === 'free'}
                onClick={() => handleSelectPlan('free')}
                leftIcon={!user ? <UserPlusIcon className="h-5 w-5" /> : undefined}
              >
                {user && currentPlan === 'free' ? 'Current Plan' : !user ? 'Get Started Free' : 'Select Plan'}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Starter Plan */}
        <motion.div variants={itemVariants}>
          <Card className={`h-full transform md:scale-105 shadow-md ${user && currentPlan === 'starter' ? 'ring-2 ring-primary-500' : ''}`}>
            <div className="bg-primary-500 text-white text-center py-2 text-sm font-medium">
              MOST POPULAR
            </div>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Starter</h2>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">$9</span>
                <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Great for regular users
              </p>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                {plans.starter.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-success-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
                <li className="flex items-start">
                  <XIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-400">Advanced AI capabilities</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>BYOK Available:</strong> Use your own OpenAI API key for unlimited access
                </p>
              </div>
            </div>
            <div className="p-6 pt-0">
              <Button
                variant={user && currentPlan === 'starter' ? 'outline' : 'primary'}
                fullWidth
                className={user && currentPlan === 'starter' ? '' : 'bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600'}
                onClick={() => handleSelectPlan('starter')}
                leftIcon={!user ? <UserPlusIcon className="h-5 w-5" /> : undefined}
              >
                {user && currentPlan === 'starter' ? 'Current Plan' : !user ? 'Get Started with Starter' : 'Upgrade to Starter'}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Pro Plan */}
        <motion.div variants={itemVariants}>
          <Card className={`h-full ${user && currentPlan === 'pro' ? 'ring-2 ring-primary-500' : ''}`}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Pro</h2>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">$29</span>
                <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                For power users and professionals
              </p>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                {plans.pro.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-success-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>BYOK Available:</strong> Use your own OpenAI API key for unlimited access
                </p>
              </div>
            </div>
            <div className="p-6 pt-0">
              <Button
                variant={user && currentPlan === 'pro' ? 'outline' : 'primary'}
                fullWidth
                onClick={() => handleSelectPlan('pro')}
                leftIcon={!user ? <UserPlusIcon className="h-5 w-5" /> : undefined}
              >
                {user && currentPlan === 'pro' ? 'Current Plan' : !user ? 'Get Started with Pro' : 'Upgrade to Pro'}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>

      <motion.div variants={itemVariants} className="mt-12">
        <Card className="p-6 bg-gradient-to-r from-primary-50 to-secondary-50 border-none">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                What happens when I run out of minutes?
              </h4>
              <p className="text-gray-600">
                When you reach your daily limit, you'll need to wait until the next day for your minutes to reset. Alternatively, you can:
              </p>
              <ul className="mt-2 ml-4 text-gray-600 list-disc space-y-1">
                <li>Upgrade to a higher plan</li>
                <li>Use your own OpenAI API key</li>
                <li>Purchase additional credits (100 or 500 credits available)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                How do I use my own OpenAI API key (BYOK)?
              </h4>
              <p className="text-gray-600">
                With BYOK (Bring Your Own Key), you can use your personal OpenAI API key for unlimited sessions, regardless of your subscription plan. Enter your key on the home screen to activate this feature.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                Can I cancel my subscription anytime?
              </h4>
              <p className="text-gray-600">
                Yes, you can downgrade or cancel your subscription at any time. Changes will take effect at the end of your current billing cycle.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                Can I use VoiceVision without subscribing?
              </h4>
              <p className="text-gray-600">
                Yes. The Free plan gives you access to basic features and 10 minutes of daily usage. You can also enable BYOK to remove these limits.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                What happens if I purchase credits?
              </h4>
              <p className="text-gray-600">
                Credits give you extended session time beyond your daily limit. You can buy credit packs (100 or 500) at any time, and they never expire.
              </p>
              <div className="mt-2">
                <button
                  onClick={() => setShowCreditsModal(true)}
                  className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                >
                  Purchase Credits →
                </button>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
      </div>

      <PurchaseCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
      
      <Footer />
    </motion.div>
  );
};

export default Subscription;